import jsPDF from 'jspdf';
import 'jspdf-autotable';

const pdfExport = {
  /**
   * Export property analysis as PDF - comprehensive report
   */
  async exportPropertyAnalysis(property, kpis, projectionData, scenarios = []) {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header with branding
      doc.setFillColor(27, 54, 93);
      doc.rect(0, 0, pageWidth, 35, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('ImmoIQ', 14, 20);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Rapport d\'analyse immobilière', 14, 28);

      // Date
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-CA')}`, pageWidth - 50, 28);

      // Reset text color
      doc.setTextColor(0, 0, 0);

      // Property title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(property?.address || 'Propriété', 14, 50);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`${property?.city || ''}, ${property?.province || 'QC'} • ${property?.property_type || 'Multiplex'}`, 14, 58);

      // Property details box
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(14, 65, pageWidth - 28, 30, 3, 3, 'S');

      const detailY = 77;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Prix d\'achat', 20, detailY);
      doc.text('Mise de fonds', 70, detailY);
      doc.text('Hypothèque', 120, detailY);
      doc.text('Taux', 170, detailY);

      doc.setFont('helvetica', 'normal');
      doc.text(pdfExport.formatCurrency(property?.purchase_price || 0), 20, detailY + 8);
      doc.text(pdfExport.formatCurrency(property?.down_payment_amount || 0), 70, detailY + 8);
      doc.text(pdfExport.formatCurrency(property?.mortgage_amount || 0), 120, detailY + 8);
      doc.text(`${(property?.interest_rate || 0).toFixed(2)}%`, 170, detailY + 8);

      // KPIs Section
      let yPos = 105;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Indicateurs de performance', 14, yPos);
      yPos += 8;

      // KPIs in a nice grid
      const kpiData = [
        ['Cashflow mensuel', pdfExport.formatCurrency(kpis?.monthlyCashflow || 0)],
        ['Cashflow annuel', pdfExport.formatCurrency(kpis?.annualCashflow || 0)],
        ['Taux de capitalisation (Cap Rate)', `${(kpis?.capRate || 0).toFixed(2)}%`],
        ['ROI Cash-on-Cash', `${(kpis?.cashOnCashReturn || 0).toFixed(2)}%`],
        ['Ratio couverture dette (DSCR)', `${(kpis?.dscr || 0).toFixed(2)}x`],
        ['NOI annuel', pdfExport.formatCurrency(kpis?.noi || 0)],
        ['Ratio dépenses/revenus', `${((kpis?.expenseRatio || 0) * 100).toFixed(1)}%`],
        ['ROI projeté', `${(kpis?.roiTotal || 0).toFixed(1)}%`]
      ];

      doc.autoTable({
        startY: yPos,
        head: [['Indicateur', 'Valeur']],
        body: kpiData,
        theme: 'striped',
        headStyles: { fillColor: [27, 54, 93], fontSize: 10 },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 60, halign: 'right' }
        }
      });

      // Projections table
      const afterKPIs = doc.lastAutoTable.finalY + 15;

      // Check if we need a new page
      if (afterKPIs > 200) {
        doc.addPage();
        yPos = 20;
      } else {
        yPos = afterKPIs;
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Projections financières sur 10 ans', 14, yPos);

      const projectionRows = projectionData?.slice(0, 11).map(p => [
        `Année ${p.year}`,
        pdfExport.formatCurrency(p.propertyValue || 0),
        pdfExport.formatCurrency(p.mortgageBalance || 0),
        pdfExport.formatCurrency(p.equity || 0),
        pdfExport.formatCurrency(p.annualCashflow || 0)
      ]);

      doc.autoTable({
        startY: yPos + 8,
        head: [['Année', 'Valeur immeuble', 'Solde hypothèque', 'Équité', 'Cashflow annuel']],
        body: projectionRows || [],
        theme: 'striped',
        headStyles: { fillColor: [27, 54, 93], fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 40, halign: 'right' },
          2: { cellWidth: 40, halign: 'right' },
          3: { cellWidth: 40, halign: 'right' },
          4: { cellWidth: 40, halign: 'right' }
        }
      });

      // Scenarios section if available
      if (scenarios && scenarios.length > 0) {
        doc.addPage();
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Scénarios de comparaison', 14, 20);

        const scenarioRows = scenarios.map(s => [
          s.name || 'Scénario',
          pdfExport.formatCurrency(s.down_payment_amount || 0),
          `${(s.interest_rate || 0).toFixed(2)}%`,
          `${s.amortization_period || 25} ans`,
          pdfExport.formatCurrency(s.monthly_cashflow || 0)
        ]);

        doc.autoTable({
          startY: 28,
          head: [['Scénario', 'Mise de fonds', 'Taux', 'Amortissement', 'Cashflow mensuel']],
          body: scenarioRows,
          theme: 'striped',
          headStyles: { fillColor: [27, 54, 93], fontSize: 10 }
        });
      }

      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${i} de ${pageCount} | ImmoIQ | ${property?.address || ''}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const cleanAddress = (property?.address || 'propriete').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const fileName = `ImmoIQ_Analyse_${cleanAddress}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      return { success: true };
    } catch (error) {
      console.error('Error exporting PDF:', error);
      return { success: false, error: error?.message || 'Erreur lors de l\'export PDF' };
    }
  },

  formatCurrency(value) {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  }
};

export default pdfExport;
